import { useState } from "react";

import PropTypes from "prop-types";
import { ulid } from "ulid";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { ChartOfAccountTypeEnum } from "@/features/chart-of-accounts";
import { formatMoney } from "@/features/shared";
import {
  CustomDialogTitle,
  CustomInputField,
  CustomNumberField,
  useSnackbar,
} from "@/features/ui";
import { OutletChartOfAccountSelect } from "../outlet-chart-of-account-select";

// Accurate only lets you pick revenue / COGS accounts as discount accounts.
const DEFAULT_DISCOUNT_ACCOUNT_TYPES = [
  ChartOfAccountTypeEnum.REVENUE,
  ChartOfAccountTypeEnum.COST_OF_GOODS_SOLD,
];

const createEmptyRow = () => ({
  id: ulid(),
  outletChartOfAccount: null,
  amount: null,
  note: "",
});

export const DiscountAccountsDialog = ({
  open,
  onClose,
  onSave,
  outletId,
  referenceNumber,
  maxAmount,
  value,
  types = DEFAULT_DISCOUNT_ACCOUNT_TYPES,
}) => {
  const { showSnackbar } = useSnackbar();
  // Editing buffer seeded from the committed value. The dialog is remounted
  // per row (via `key`), so the initializer re-runs for each invoice line.
  const [rows, setRows] = useState(() =>
    value?.length ? value.map((row) => ({ ...row })) : []
  );

  const total = rows.reduce((acc, row) => acc + (row.amount || 0), 0);

  const updateRow = (id, key, newValue) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: newValue } : row))
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleSave = () => {
    if (rows.some((row) => !row.outletChartOfAccount)) {
      showSnackbar("Akun diskon harus dipilih", "error");
      return;
    }

    if (rows.some((row) => !row.amount || row.amount <= 0)) {
      showSnackbar("Nilai diskon harus lebih besar dari 0", "error");
      return;
    }

    if (maxAmount != null && total > maxAmount) {
      showSnackbar(
        `Total diskon tidak boleh lebih besar dari jumlah bayar (max: ${formatMoney(
          maxAmount
        )})`,
        "error"
      );
      return;
    }

    onSave(rows);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <CustomDialogTitle
        title={
          referenceNumber
            ? `Informasi Diskon - ${referenceNumber}`
            : "Informasi Diskon"
        }
        onClose={onClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          {rows.length === 0 ? (
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Belum ada akun diskon. Tekan &quot;Tambah Akun Diskon&quot; untuk
              menambah.
            </Typography>
          ) : (
            rows.map((row) => (
              <Grid
                key={row.id}
                container
                spacing={1.5} sx={{ alignItems: "flex-start" }}>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <OutletChartOfAccountSelect
                    value={row.outletChartOfAccount}
                    onChange={(newValue) =>
                      updateRow(row.id, "outletChartOfAccount", newValue)
                    }
                    outletId={outletId}
                    types={types}
                    TextFieldProps={{
                      InputProps: { placeholder: "Pilih Akun Diskon" },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <CustomNumberField
                    value={row.amount ?? ""}
                    onChange={(e) =>
                      updateRow(
                        row.id,
                        "amount",
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                    InputProps={{
                      placeholder: "Diskon",
                      startAdornment: (
                        <InputAdornment position="start">Rp</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <CustomInputField
                    value={row.note}
                    onChange={(e) => updateRow(row.id, "note", e.target.value)}
                    InputProps={{ placeholder: "Keterangan" }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveRow(row.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            sx={{ alignSelf: "flex-start" }}
          >
            Tambah Akun Diskon
          </Button>

          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              pt: 1
            }}>
            <Typography sx={{
              fontWeight: "bold"
            }}>Total Diskon</Typography>
            <Typography sx={{
              fontWeight: "bold"
            }}>{formatMoney(total)}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button variant="contained" onClick={handleSave}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DiscountAccountsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  outletId: PropTypes.string,
  referenceNumber: PropTypes.string,
  maxAmount: PropTypes.number,
  value: PropTypes.array,
  types: PropTypes.arrayOf(PropTypes.string),
};
